import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { weeklyReportsApi, type OrderReportSettingsPayload } from "@/features/orders/api/weeklyReports";

export function useWeeklyReports(orderId?: string, refetchInterval?: number) {
  const params: Record<string, string> = {};
  if (orderId) params.order = orderId;
  return useQuery({
    queryKey: ["weekly-reports", params],
    queryFn: () => weeklyReportsApi.list(params),
    enabled: orderId === undefined || !!orderId,
    refetchInterval,
  });
}

export function useWeeklyReport(id?: string) {
  return useQuery({
    queryKey: ["weekly-report", id],
    queryFn: () => weeklyReportsApi.get(id!),
    enabled: !!id,
  });
}

export function useWeeklyReportPendingCount(enabled = true) {
  return useQuery({
    queryKey: ["weekly-reports-pending-count"],
    queryFn: weeklyReportsApi.pendingCount,
    enabled,
  });
}

export function useWeeklyReportUnreadCount(enabled = true) {
  return useQuery({
    queryKey: ["weekly-reports-unread-count"],
    queryFn: weeklyReportsApi.unreadCount,
    enabled,
  });
}

export function useSubmitWeeklyReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, files }: { id: string; files: File[] }) =>
      weeklyReportsApi.submit(id, files),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weekly-reports"] });
      qc.invalidateQueries({ queryKey: ["weekly-reports-pending-count"] });
    },
  });
}

export function useAcceptWeeklyReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => weeklyReportsApi.accept(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weekly-reports"] });
      qc.invalidateQueries({ queryKey: ["weekly-reports-unread-count"] });
    },
  });
}

export function useRejectWeeklyReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      weeklyReportsApi.reject(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weekly-reports"] });
      qc.invalidateQueries({ queryKey: ["weekly-reports-unread-count"] });
    },
  });
}

export function useMarkWeeklyReportRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => weeklyReportsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weekly-reports"] });
      qc.invalidateQueries({ queryKey: ["weekly-reports-unread-count"] });
    },
  });
}

export function useUpdateOrderReportSettings(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: OrderReportSettingsPayload) =>
      weeklyReportsApi.updateOrderReportSettings(orderId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["weekly-reports"] });
    },
  });
}
